import type { DateTime } from "luxon";
import type { FC } from "react";
import { MultiCommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { InHouseTableDiffChart } from "../../../components/UIToolkit/Graphs/TableDiffChart/InHouseTableDiffChart";
import type { ManagementCenterMultiDepartmentItem } from "../../../utils/models";
import { useManagementCenterDownload } from "../hooks/useManagementCenterDownload";
import { useManagementCenterMultiAgencyUse } from "../hooks/useManagementCenterMulti";

interface IProps {
	maxRows: number;
	width: string;
	selectedRegions: string[];
	handleItemDoubleClick: (departmentName: string) => void;
	startDate: DateTime;
	endDate: DateTime;
}

export const AgencyUseChart: FC<IProps> = ({ maxRows, width, selectedRegions, handleItemDoubleClick, startDate, endDate }) => {
	const { data, isLoading } = useManagementCenterMultiAgencyUse({ startDate, endDate });
	const { download } = useManagementCenterDownload({ graphName: "agency-use", startDate, endDate });
	const rawData = data?.items || [];
	const filteredData = selectedRegions.length === 0 ? rawData : rawData.filter((item) => selectedRegions.includes(item.region));

	const items = filteredData.map((item: ManagementCenterMultiDepartmentItem) => ({
		...item,
		title: `${item.department}, ${item.region}`,
		onDoubleClick: () => handleItemDoubleClick(item.department),
	}));

	const networkAvgActual = items.length > 0 ? Math.round(items.reduce((sum, item) => sum + item.actual, 0) / items.length) : undefined;

	// Threshold for Agency Use: lower percentages are better
	// warning: 100 = at target, error: 150 = 50% over target
	const threshold = {
		warning: 100,
		error: 150,
	};

	const actualWrapper = (value: number) => {
		return `${value}%`;
	};

	return (
		<MultiCommonDashboardBox sx={{ display: "flex", flexDirection: "column", alignItems: "start", width }}>
			<InHouseTableDiffChart
				title="Agency Usage"
				subTitle={networkAvgActual !== undefined ? `Network Avg - ${networkAvgActual}%` : "Network Avg - N/A"}
				items={items}
				isGoodFirst
				threshold={threshold}
				higherIsBetter={false}
				sortByBest={false}
				badSortingOrder
				actualWrapper={actualWrapper}
				maxVisibleItems={maxRows}
				isLoading={isLoading}
				onDownload={download}
			/>
		</MultiCommonDashboardBox>
	);
};
